import { useState, useEffect } from "react";
import { WifiOff, Wifi, RefreshCw, Check, CloudOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOfflineData } from "@/contexts/OfflineDataContext";

const OfflineIndicator = () => {
  const { isOnline, syncing, lastSync, syncAllData, dataReady } = useOfflineData();
  const [showBanner, setShowBanner] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShowBanner(true);
      setWasOffline(true);
    } else if (wasOffline) {
      setShowReconnected(true);
      setShowBanner(true);
      setWasOffline(false);
      // Auto-sync when coming back online
      syncAllData();
      setTimeout(() => {
        setShowReconnected(false);
        setShowBanner(false);
      }, 4000);
    }
  }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatSyncTime = (timestamp) => {
    if (!timestamp) return "Never";
    const diff = Date.now() - timestamp;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Offline banner
  if (!isOnline) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-amber-600 text-white px-4 py-3 shadow-lg"
        data-testid="offline-banner"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CloudOff className="w-5 h-5 shrink-0" />
            <div>
              <span className="font-medium text-sm">You are offline</span>
              <p className="text-xs opacity-90">
                {lastSync
                  ? `Using data synced ${formatSyncTime(lastSync)}`
                  : "No cached data available. Connect to sync."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reconnected toast
  if (showReconnected) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium bg-green-500 text-white"
        data-testid="reconnected-toast"
      >
        <Wifi className="w-4 h-4" />
        <span>Back online — syncing data...</span>
      </div>
    );
  }

  return null;
};

// Sync Status widget for the Settings page or header
export const SyncStatus = () => {
  const { lastSync, syncing, syncAllData, isOnline, protocols, drugs, procedures } = useOfflineData();

  const formatSyncTime = (timestamp) => {
    if (!timestamp) return "Never synced";
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="space-y-3" data-testid="sync-status">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-sm">Offline Data</h4>
          <p className="text-xs text-muted-foreground">
            Last synced: {formatSyncTime(lastSync)}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={syncAllData}
          disabled={syncing || !isOnline}
          data-testid="sync-now-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? "Syncing..." : "Sync Now"}
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-secondary/20 rounded p-2">
          <div className="font-bold text-lg">{protocols.length}</div>
          <div className="text-xs text-muted-foreground">Protocols</div>
        </div>
        <div className="bg-secondary/20 rounded p-2">
          <div className="font-bold text-lg">{drugs.length}</div>
          <div className="text-xs text-muted-foreground">Medications</div>
        </div>
        <div className="bg-secondary/20 rounded p-2">
          <div className="font-bold text-lg">{procedures.length}</div>
          <div className="text-xs text-muted-foreground">Procedures</div>
        </div>
      </div>
      {!isOnline && (
        <div className="text-xs text-amber-500 flex items-center gap-1">
          <CloudOff className="w-3 h-3" />
          Offline — using cached data
        </div>
      )}
      {lastSync && isOnline && (
        <div className="text-xs text-green-500 flex items-center gap-1">
          <Check className="w-3 h-3" />
          Data available offline
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
